import { getCountryCode } from "../helpers/ui";
import config from "../config";
import { logError } from "../utils/index";

export const postDonation = async (donation) => {
  const country = getCountryCode(donation.province.toUpperCase());
  const displayOnAir = !donation.doNotDisplayDonation;

  if (donation.doNotDisplayDonation) {
    donation.display1 = "";
    donation.display2 = "";
  }

  const data = new FormData();
  data.append("donation[key]", "1ded8111-d6c1-4352-96a0-9c8dba48f8f0");
  data.append("donation[company]", donation.companyName.toUpperCase());
  data.append("donation[first]", donation.firstName.toUpperCase());
  data.append("donation[last]", donation.lastName.toUpperCase());
  data.append("donation[address]", donation.address1.toUpperCase());
  data.append("donation[address2]", donation.address2.toUpperCase());
  data.append("donation[apartment_no]", donation.aptNo.toUpperCase());
  data.append("donation[po_box]", donation.poBox.toUpperCase());
  data.append("donation[city]", donation.city.toUpperCase());
  data.append("donation[province]", donation.province.toUpperCase());
  data.append("donation[postal_code]", donation.postalCode.toUpperCase());
  data.append("donation[country]", country);
  data.append("donation[email]", donation.email.toUpperCase());
  data.append("donation[phone]", donation.telephone.toUpperCase());
  data.append("donation[donation_method]", "Credit Card");
  data.append("donation[amount]", donation.donationAmount);
  data.append("donation[chyron]", displayOnAir);
  data.append("donation[prime_display_1]", donation.display1.toUpperCase());
  data.append("donation[prime_display_2]", donation.display2.toUpperCase());
  data.append("donation[redirect_donation]", 1);
  data.append("donation[redirect_province]", donation.region.toUpperCase());
  data.append("donation[web_donation]", 1);
  data.append("credit_card[credit_card_number]", donation.creditCard);
  data.append("credit_card[cvv]", donation.cvv);
  data.append("credit_card[exp_month]", donation.expiryMonth);
  data.append("credit_card[exp_year]", donation.expiryYear);

  const identifier = donation.lastName.toUpperCase() + "-" + donation.firstName.toUpperCase();

  try {
    const response = await fetch(config.donationUrl, {
      method: "POST",
      body: data,
    });

    const result = await response.json();

    if (!result.message) {
      // VALIDATION ERRORS
      const receipt = {
        status: "VALIDATION-ERRORS",
        errors: formatValidationErrors(result),
      };

      return receipt;
    }

    let receipt = {
      status: result.message.toUpperCase(),
      transactionDate: getCurrentDate(),
      transactionAmount: result.amount || 0,
      creditCard: formatCreditCard(donation.creditCard),
      cardHolderName: (donation.firstName + " " + donation.lastName).toUpperCase(),
      companyName: donation.companyName.toUpperCase(),
      display1: donation.display1.toUpperCase(),
      display2: donation.display2.toUpperCase(),
      displayOnTv: !donation.doNotDisplayDonation,
    };

    if (receipt.status === "DECLINED" || receipt.status === "DECLINE") {
      // DECLINED
      receipt.status = "DECLINED";
      receipt.message = `Your transaction was declined by processor.`;
      receipt.authorizationCode = "";
      receipt.transactionNumber = result.transaction_id;
      receipt.transactionAmount = donation.donationAmount;
    } else if (receipt.status === "APPROVED") {
      // APPROVED
      receipt.message = "Thank you for your donation!";
      receipt.transactionNumber = result.id;
      receipt.authorizationCode = result.auth_code;
    } else {
      // ERROR
      receipt.status = "ERROR";
      receipt.transactionAmount = donation.donationAmount;
      receipt.message = "We were unable to confirm the result of your donation.";

      try {
        logError({ identifier, errorMessage: "Unexpected donation response" });
      } catch (error) {
        // Logging must not affect the donation result.
      }
    }

    return receipt;
  } catch (e) {
    const receipt = {
      status: "ERROR",
      message: "We were unable to confirm the result of your donation.",
      transactionDate: getCurrentDate(),
      authorizationCode: "",
      transactionAmount: donation.donationAmount,
      creditCard: formatCreditCard(donation.creditCard),
      cardHolderName: (donation.firstName + " " + donation.lastName).toUpperCase(),
      companyName: donation.companyName.toUpperCase(),
      display1: donation.display1.toUpperCase(),
      display2: donation.display2.toUpperCase(),
      displayOnTv: !donation.doNotDisplayDonation,
    };

    try {
      logError({ identifier, errorMessage: "Network or response error" });
    } catch (error) {
      // Logging must not affect the donation result.
    }

    return receipt;
  }
};

const getCurrentDate = () => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, "0");
  const day = String(today.getDate()).padStart(2, "0");
  return `${year}/${month}/${day}`;
};

const formatCreditCard = (cardNumber) => {
  try {
    if (cardNumber == null || cardNumber === "") {
      return "************";
    }

    const digits = String(cardNumber).replace(/\D/g, "");
    if (digits.length < 4) {
      return "************";
    }

    const cardType = getCardType(digits);
    const visibleStart = digits.slice(0, 4);
    const visibleEnd = cardType === "amex" ? digits.slice(-5) : digits.slice(-4);
    const maskedLength = Math.max(0, digits.length - visibleStart.length - visibleEnd.length);

    return `${visibleStart}${"*".repeat(maskedLength)}${visibleEnd}`;
  } catch {
    return "************";
  }
};

const getCardType = (cardNumber) => {
  const firstDigit = cardNumber.charAt(0);
  const firstTwoDigits = cardNumber.slice(0, 2);

  if (firstDigit === "4") return "visa";
  if (["51", "52", "53", "54", "55"].includes(firstTwoDigits)) return "mastercard";
  if (["34", "37"].includes(firstTwoDigits)) return "amex";
  return null;
};

const formatValidationErrors = (validationErrors) => {
  let errorMessage = "";

  for (const [field, errors] of Object.entries(validationErrors)) {
    errorMessage += `<p><strong>${field.charAt(0).toUpperCase() + field.slice(1)}:</strong> `;

    errors.forEach((error, index) => {
      errorMessage += error;
      if (index < errors.length - 1) {
        errorMessage += ", ";
      }
    });

    errorMessage += ".</p>";
  }

  return errorMessage;
};
